import { UrlTree } from '@angular/router';

/**
 * The seven ways opening a guest link can end.
 *
 * Every entry path must terminate in exactly one of these; no path may end without one
 * (FR-001, FR-004). This replaces the implicit branching scattered across the two guards.
 */
export enum ELinkOutcome {
  /** The document endpoint answered 200 and the workspace can load. */
  USABLE = 'usable',
  /** The document is already finished. Existing behaviour, unchanged (FR-023). */
  COMPLETED = 'completed',
  /** The link's own token expired. Observers are exempt, which the backend decides. */
  EXPIRED_TOKEN = 'expiredToken',
  /** The document's own validity lapsed. Applies to every link type, observers included. */
  EXPIRED_DOCUMENT = 'expiredDocument',
  /** The document went back to draft: cancelled, `to_draft`, or a flow change (D-10). */
  UNAVAILABLE = 'unavailable',
  /** The token could not be read at all — the link arrived cut (FR-027). */
  TRUNCATED = 'truncated',
  /** Anything else, including offline and malformed responses (FR-003). */
  UNKNOWN = 'unknown',
}

/**
 * Where each outcome sends the recipient.
 *
 * `/expired` and `/expired-link` already exist; `/unavailable`, `/unreadable-link` and
 * `/link-error` are added by this feature. All of them are registered beside the other status
 * routes rather than under the authenticated shell — the recipient has no valid token, so the
 * shell would never load (documented FE-R1 deviation).
 */
export const LINK_OUTCOME_ROUTES: Readonly<Record<ELinkOutcome, string>> = {
  [ELinkOutcome.USABLE]: '',
  [ELinkOutcome.COMPLETED]: '/signed',
  [ELinkOutcome.EXPIRED_TOKEN]: '/expired-link',
  [ELinkOutcome.EXPIRED_DOCUMENT]: '/expired',
  [ELinkOutcome.UNAVAILABLE]: '/unavailable',
  [ELinkOutcome.TRUNCATED]: '/unreadable-link',
  [ELinkOutcome.UNKNOWN]: '/link-error',
};

/**
 * A resolved outcome and where it sends the recipient.
 *
 * `urlTree` is `null` only for {@link ELinkOutcome.USABLE}, where the guard lets the navigation
 * through instead of redirecting.
 */
export interface ILinkOutcomeResolution {
  outcome: ELinkOutcome;
  urlTree: UrlTree | null;
}

/**
 * The error shape the SPA's HTTP layer produces, after `BaseService.handleError` normalizes it.
 *
 * The two families are normalized differently, which is why both spellings appear here:
 *
 * - `400`, `404`, `422`, `500` — the body is spread onto the object with **every key
 *   lower-cased**, so `codeName` arrives as `codename`.
 * - `401` — the body is assigned whole to `message`, keeping its original camelCase.
 *
 * Both carry `httpStatus`. The raw `status` does not survive normalization and must never be
 * read (FR-028).
 */
export interface INormalizedHttpError {
  httpStatus?: number;
  /** Present on the 400 family: the body's `codeName`, lower-cased by the normalizer. */
  codename?: string;
  /** On 401 this holds the parsed body; on the 400 family it is the message text. */
  message?: string | INormalizedErrorBody | null;
}

/**
 * The `ServiceResponseError` body the backend returns, as it survives a 401 normalization.
 */
export interface INormalizedErrorBody {
  message?: string;
  code?: number;
  codeName?: string;
}
